import { useState } from "react";
import { motion } from "framer-motion";
import { Plus, Minus } from "lucide-react";

const EASE = [0.76, 0, 0.24, 1];

/**
 * Accessible FAQ accordion. Pair with faqSchema() to inject FAQPage JSON-LD.
 * Props: { title, kicker, items:[{q, a}] }
 */
export const FaqBlock = ({ title, kicker, items = [] }) => {
  const [open, setOpen] = useState(null);
  return (
    <section className="bg-[var(--bg)] py-24 md:py-32 border-t border-[var(--line-strong)]">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-12">
        <div className="md:col-span-4">
          {kicker && <p className="overline mb-4">{kicker}</p>}
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.8, ease: EASE }}
            className="font-display text-3xl md:text-5xl tracking-[-0.02em] leading-[1.05] text-[var(--ink)]"
          >
            {title}
          </motion.h2>
        </div>
        <div className="md:col-span-8 border-t border-[var(--line-strong)]">
          {items.map((item, i) => {
            const isOpen = open === i;
            return (
              <div
                key={item.q}
                className="border-b border-[var(--line-strong)]"
                data-testid={`faq-item-${i}`}
              >
                <button
                  onClick={() => setOpen(isOpen ? null : i)}
                  aria-expanded={isOpen}
                  className="w-full py-6 flex items-start justify-between gap-6 text-left group"
                >
                  <span className="font-display text-lg md:text-xl text-[var(--ink)] leading-snug pr-4">
                    {item.q}
                  </span>
                  <span className="mt-1 text-[var(--accent)] shrink-0">
                    {isOpen ? (
                      <Minus className="w-5 h-5" strokeWidth={1.8} />
                    ) : (
                      <Plus className="w-5 h-5" strokeWidth={1.8} />
                    )}
                  </span>
                </button>
                <motion.div
                  initial={false}
                  animate={{
                    height: isOpen ? "auto" : 0,
                    opacity: isOpen ? 1 : 0,
                  }}
                  transition={{ duration: 0.4, ease: EASE }}
                  className="overflow-hidden"
                >
                  <p className="pb-6 pr-8 text-[var(--ink-80)] leading-relaxed max-w-2xl">
                    {item.a}
                  </p>
                </motion.div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default FaqBlock;
